import React from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    IconButton,
    Grid,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import {
    ArrowForward as BackIcon,
    Search as SearchIcon,
    Save as SaveIcon,
    Lock as LockIcon,
    CalendarToday as CalendarIcon,
    CheckCircle as CheckIcon,
    Description as ReportIcon,
    Warning as WarningIcon,
    History as HistoryIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { styles, datePickerPopupSlotProps } from '../../styles/ChemoRecordsStyles';
import { useChemoReportForm, convertToBoolean, formatDateForInput } from '../../hooks/chemo/useChemoReportForm';
import { chemoTranslations } from '../../hooks/chemo/chemoTranslations';
import { HOSPITALS_WITH_DATE_EDIT } from '../../hooks/chemo/chemoConstants';

const ChemoReportForm = ({
    report,
    onSubmit,
    onCancel,
    hospital,
    fetchedRecords = [],
    isLoadingRecords = false,
    error = null,
    handleCheckboxChange,
    handleFieldChange,
    formatDate,
    onSave,
    onLoadPrevious
}) => {
    const {
        formData,
        warningDialog,
        dateWarningDialog,
        handleFormChange,
        handleSubmit,
        handleSave,
        handleWarningClose,
        handleDateWarningClose,
        handleSaveAnyway,
        handleSaveWithDifferentDates,
        isRecordLocked,
        getPreferredDueDate,
        getUnchangedRecordsMessage,
        getFormTitle
    } = useChemoReportForm(report, hospital, fetchedRecords, onSubmit, onSave);

    return (
        <Box sx={styles.formContainer}>
            <Paper sx={styles.formPaper}>
                <Box sx={styles.formHeader}>
                    <IconButton
                        onClick={onCancel}
                        sx={{
                            color: '#fff',
                            '&:hover': {
                                backgroundColor: 'rgba(255,255,255,0.15)'
                            }
                        }}
                    >
                        <BackIcon />
                    </IconButton>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <ReportIcon sx={{ fontSize: 28 }} />
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            {getFormTitle()}
                        </Typography>
                    </Box>
                    <Box sx={{ width: 40 }} />
                </Box>

                <Box sx={{ padding: { xs: '16px', sm: '24px', md: '32px' } }}>
                    <Box sx={{
                        marginBottom: { xs: '24px', md: '32px' },
                        padding: { xs: '16px', sm: '20px', md: '24px' },
                        backgroundColor: '#f8fafb',
                        borderRadius: { xs: '8px', md: '12px' },
                        border: '1px solid #e3f2fd'
                    }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            marginBottom: { xs: '16px', md: '24px' },
                            paddingBottom: '16px',
                            borderBottom: '1px solid #e3f2fd',
                            gap: { xs: 2, sm: 0 }
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <CalendarIcon sx={{ color: '#5e72e4', fontSize: { xs: 20, sm: 22, md: 24 } }} />
                                <Typography variant="h6" sx={{
                                    fontWeight: 600,
                                    color: '#37474f',
                                    fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
                                }}>
                                    {chemoTranslations.form.reportParams}
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                startIcon={<SearchIcon />}
                                onClick={handleSubmit}
                                disabled={isLoadingRecords}
                                sx={{
                                    backgroundColor: '#3498db',
                                    fontWeight: 600,
                                    padding: { xs: '8px 20px', sm: '9px 22px', md: '10px 24px' },
                                    borderRadius: { xs: '8px', md: '10px' },
                                    boxShadow: '0 4px 12px rgba(52, 152, 219, 0.25)',
                                    textTransform: 'none',
                                    fontSize: { xs: '14px', md: '15px' },
                                    width: { xs: '100%', sm: 'auto' },
                                    '&:hover': {
                                        backgroundColor: '#2980b9',
                                        boxShadow: '0 6px 16px rgba(52, 152, 219, 0.35)'
                                    },
                                    '&:disabled': { backgroundColor: '#cfd8dc' }
                                }}
                            >
                                {chemoTranslations.form.searchReport}
                            </Button>
                        </Box>

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <DatePicker
                                    label={chemoTranslations.form.startDate}
                                    format="DD/MM/YYYY"
                                    value={formData.startDate ? dayjs(formData.startDate) : null}
                                    onChange={(val) => handleFormChange('startDate', val ? val.format('YYYY-MM-DD') : '')}
                                    maxDate={dayjs()}
                                    slotProps={{ ...datePickerPopupSlotProps, textField: { fullWidth: true, sx: styles.formTextField } }}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    label={chemoTranslations.form.endDate}
                                    format="DD/MM/YYYY"
                                    value={formData.endDate ? dayjs(formData.endDate) : null}
                                    onChange={(val) => handleFormChange('endDate', val ? val.format('YYYY-MM-DD') : '')}
                                    maxDate={dayjs()}
                                    slotProps={{ ...datePickerPopupSlotProps, textField: { fullWidth: true, sx: styles.formTextField } }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={chemoTranslations.form.hospital}
                                    value={formData.hospital}
                                    InputProps={{ readOnly: true }}
                                    sx={styles.formTextFieldReadOnly}
                                />
                            </Grid>
                        </Grid>
                        </LocalizationProvider>
                    </Box>

                    {isLoadingRecords ? (
                        <LoadingState />
                    ) : error ? (
                        <ErrorState error={error} />
                    ) : fetchedRecords.length > 0 ? (
                        <RecordsSection
                            fetchedRecords={fetchedRecords}
                            isRecordLocked={isRecordLocked}
                            getPreferredDueDate={getPreferredDueDate}
                            handleCheckboxChange={handleCheckboxChange}
                            handleFieldChange={handleFieldChange}
                            formatDate={formatDate}
                            handleSave={handleSave}
                            isLoadingRecords={isLoadingRecords}
                            onLoadPrevious={onLoadPrevious}
                            hospital={hospital}
                        />
                    ) : (
                        <EmptyState />
                    )}
                </Box>
            </Paper>

            <WarningDialog
                open={warningDialog.open}
                unmodifiedRecords={warningDialog.unmodifiedRecords}
                onClose={handleWarningClose}
                onSaveAnyway={handleSaveAnyway}
                getUnchangedRecordsMessage={getUnchangedRecordsMessage}
            />

            <DateWarningDialog
                open={dateWarningDialog.open}
                differentDates={dateWarningDialog.differentDates}
                onClose={handleDateWarningClose}
                onContinue={handleSaveWithDifferentDates}
            />
        </Box>
    );
};

const LoadingState = () => (
    <Box sx={{
        marginTop: { xs: '24px', md: '32px' },
        textAlign: 'center',
        padding: { xs: '40px 16px', sm: '52px 24px', md: '64px' },
        backgroundColor: '#f8fafb',
        borderRadius: { xs: '8px', md: '12px' },
        border: '2px dashed #e3f2fd'
    }}>
        <CircularProgress thickness={4} sx={{ color: '#5e72e4', marginBottom: { xs: '16px', md: '20px' } }} />
        <Typography variant="h6" sx={{ color: '#546e7a', fontWeight: 600, marginTop: '12px' }}>
            {chemoTranslations.form.loadingData}
        </Typography>
        <Typography variant="body2" sx={{ color: '#90a4ae', marginTop: '8px' }}>
            {chemoTranslations.form.pleaseWait}
        </Typography>
    </Box>
);

const ErrorState = ({ error }) => (
    <Box sx={{
        marginTop: { xs: '24px', md: '32px' },
        textAlign: 'center',
        padding: { xs: '40px 16px', sm: '52px 24px', md: '64px' },
        backgroundColor: '#ffebee',
        borderRadius: { xs: '8px', md: '12px' },
        border: '2px solid #ef5350'
    }}>
        <Typography variant="h6" sx={{ color: '#c62828', fontWeight: 600, marginBottom: '12px' }}>
            {chemoTranslations.errors.errorOccurred}
        </Typography>
        <Typography variant="body1" sx={{ color: '#d32f2f' }}>{error}</Typography>
    </Box>
);

const EmptyState = () => (
    <Box sx={styles.emptyState}>
        <Box sx={{
            width: '80px', height: '80px', borderRadius: '50%',
            backgroundColor: '#f8fafb', display: 'flex', alignItems: 'center',
            justifyContent: 'center', marginBottom: '8px', border: '2px solid #e3f2fd'
        }}>
            <SearchIcon sx={{ fontSize: 40, color: '#90a4ae' }} />
        </Box>
        <Typography variant="h6" sx={{ color: '#546e7a', fontWeight: 600 }}>
            {chemoTranslations.form.noDataToDisplay}
        </Typography>
        <Typography variant="body2" sx={{ color: '#90a4ae' }}>
            {chemoTranslations.form.useFormToSearch}
        </Typography>
    </Box>
);

const RecordsSection = ({ fetchedRecords, isRecordLocked, getPreferredDueDate, handleCheckboxChange, handleFieldChange, formatDate, handleSave, isLoadingRecords, onLoadPrevious, hospital }) => (
    <Box sx={{ marginTop: { xs: '24px', md: '32px' } }}>
        <Box sx={{
            display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' },
            marginBottom: { xs: '16px', md: '20px' },
            padding: { xs: '12px 16px', sm: '14px 18px', md: '16px 20px' },
            backgroundColor: '#f8fafb',
            borderRadius: { xs: '8px', md: '10px' },
            border: '1px solid #e8edf2',
            borderLeft: '4px solid #3498db',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            gap: { xs: 2, sm: 0 }
        }}>
            <Typography variant="h6" sx={{ color: '#1e293b', fontWeight: 700, display: 'flex', alignItems: 'center', gap: { xs: '8px', md: '12px' } }}>
                <CheckIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 }, color: '#3498db' }} />
                {chemoTranslations.form.treatmentRecords}
            </Typography>
            <Chip
                label={`${fetchedRecords.length} ${chemoTranslations.labels.records}`}
                sx={{ backgroundColor: '#3498db', color: '#fff', fontWeight: 700, width: { xs: '100%', sm: 'auto' } }}
            />
        </Box>

        <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
            <DesktopTable fetchedRecords={fetchedRecords} isRecordLocked={isRecordLocked} getPreferredDueDate={getPreferredDueDate} handleCheckboxChange={handleCheckboxChange} handleFieldChange={handleFieldChange} formatDate={formatDate} hospital={hospital} />
        </Box>
        <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
            <MobileCards fetchedRecords={fetchedRecords} isRecordLocked={isRecordLocked} getPreferredDueDate={getPreferredDueDate} handleCheckboxChange={handleCheckboxChange} handleFieldChange={handleFieldChange} formatDate={formatDate} hospital={hospital} />
        </Box>

        <Box sx={{ ...styles.formActions, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 2 } }}>
            {onLoadPrevious && (
                <Button
                    variant="outlined" size="large" startIcon={<HistoryIcon />}
                    onClick={onLoadPrevious} disabled={isLoadingRecords}
                    sx={{ ...styles.cancelButton, minWidth: { xs: '100%', sm: '200px' }, padding: { xs: '12px 24px', sm: '14px 32px' } }}
                >
                    {chemoTranslations.form.loadPreviousReport}
                </Button>
            )}
            <Button
                variant="contained" size="large" startIcon={<SaveIcon />}
                onClick={handleSave} disabled={isLoadingRecords}
                sx={{ ...styles.primaryButton, minWidth: { xs: '100%', sm: '220px' }, padding: { xs: '12px 32px', sm: '14px 40px' } }}
            >
                {chemoTranslations.form.saveReport}
            </Button>
        </Box>
    </Box>
);

const DesktopTable = ({ fetchedRecords, isRecordLocked, getPreferredDueDate, handleCheckboxChange, handleFieldChange, formatDate, hospital }) => {
const canEditDate = HOSPITALS_WITH_DATE_EDIT.includes(hospital);
return (
    <TableContainer component={Paper} sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: { xs: '8px', md: '12px' }, border: '1px solid #e3f2fd', overflow: 'auto' }} dir="rtl">
        <Table sx={{ minWidth: 1100 }} size="small">
            <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafb' }}>
                    {[
                        chemoTranslations.table.row, chemoTranslations.table.deliveryDate,
                        chemoTranslations.table.customerName, chemoTranslations.table.customerNumber,
                        chemoTranslations.table.day, chemoTranslations.table.districtDescription,
                        chemoTranslations.table.willArrive, chemoTranslations.table.willNotArrive,
                        chemoTranslations.table.endTreatment, chemoTranslations.table.updateDate,
                        chemoTranslations.table.remainingDoses,
                        chemoTranslations.table.lastDate
                    ].map((header) => (
                        <TableCell key={header} align="center" sx={styles.tableHeaderCell}>{header}</TableCell>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {fetchedRecords.map((record, index) => {
                    const lockStatus = isRecordLocked(record);
                    const preferredDueDate = getPreferredDueDate(record);
                    return (
                        <TableRow key={record.ORDI1 || index} sx={{
                            '&:nth-of-type(odd)': { backgroundColor: '#fafbfc' },
                            '&:nth-of-type(even)': { backgroundColor: '#fff' },
                            '&:hover': { backgroundColor: '#e3f2fd' },
                            opacity: lockStatus.locked ? 0.5 : 1,
                            borderLeft: lockStatus.locked ? '3px solid #ef5350' : '3px solid transparent'
                        }}>
                            <TableCell align="center" sx={styles.tableCell}>
                                {lockStatus.locked && <Tooltip title={lockStatus.reason} arrow><LockIcon sx={{ fontSize: '1rem', color: '#ef5350', mr: '6px', verticalAlign: 'middle' }} /></Tooltip>}
                                {record.KLINE2}
                            </TableCell>
                            <TableCell align="center" sx={styles.tableCell}>{formatDate(preferredDueDate)}</TableCell>
                            <TableCell align="right" sx={{ ...styles.tableCell, direction: 'rtl', textAlign: 'right' }}>{record.CDES}</TableCell>
                            <TableCell align="center" sx={{ ...styles.tableCell, color: '#3498db', fontWeight: 600 }}>{record.CUSTNAME}</TableCell>
                            <TableCell align="center" sx={styles.tableCell}>{record.DAY}</TableCell>
                            <TableCell align="right" sx={{ ...styles.tableCell, direction: 'rtl', textAlign: 'right', maxWidth: '200px' }}>{record.CUSTDES}</TableCell>
                            {[
                                ['APPROVED', '#e8f5e9', '#388e3c'],
                                ['NOTAPPROVED', '#ffebee', '#d32f2f'],
                                ['FINISH', '#e8eaf6', '#3949ab']
                            ].map(([field, bg, color]) => {
                                const isChecked = convertToBoolean(record[field]);
                                return (
                                    <TableCell key={field} align="center" sx={{ padding: '6px 4px' }}>
                                        <Tooltip title={(lockStatus.locked || lockStatus.statusLocked) ? (lockStatus.reason || lockStatus.statusReason) : ''} arrow>
                                            <span>
                                                <Chip
                                                    size="small"
                                                    label={isChecked ? '✓' : '—'}
                                                    onClick={() => !(lockStatus.locked || lockStatus.statusLocked) && handleCheckboxChange(index, field)}
                                                    sx={{
                                                        height: 24,
                                                        minWidth: 32,
                                                        fontSize: isChecked ? '0.85rem' : '0.8rem',
                                                        fontWeight: 700,
                                                        cursor: (lockStatus.locked || lockStatus.statusLocked) ? 'default' : 'pointer',
                                                        transition: 'all 0.15s ease',
                                                        backgroundColor: isChecked ? bg : 'transparent',
                                                        color: isChecked ? color : '#cbd5e0',
                                                        border: isChecked ? 'none' : '1px dashed #d1d5db',
                                                        boxShadow: isChecked ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                                                        '&:hover': { opacity: (lockStatus.locked || lockStatus.statusLocked) ? 1 : 0.75, transform: (lockStatus.locked || lockStatus.statusLocked) ? 'none' : 'scale(1.08)' },
                                                        '& .MuiChip-label': { px: '8px' }
                                                    }}
                                                />
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                );
                            })}
                            <TableCell align="center" sx={{ padding: '8px 4px' }}>
                                {canEditDate ? (
                                    <Tooltip title={(lockStatus.locked || lockStatus.dateLocked) ? (lockStatus.reason || lockStatus.dateReason) : ''} arrow>
                                        <span>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                format="DD/MM/YYYY"
                                                value={formatDateForInput(record.TODUEDATE) ? dayjs(formatDateForInput(record.TODUEDATE)) : null}
                                                onChange={(val) => !(lockStatus.locked || lockStatus.dateLocked) && handleFieldChange(index, 'TODUEDATE', val ? val.format('YYYY-MM-DD') : '')}
                                                disabled={lockStatus.locked || lockStatus.dateLocked}
                                                slotProps={{ ...datePickerPopupSlotProps, textField: { size: 'small', sx: styles.tableDateField } }}
                                            />
                                            </LocalizationProvider>
                                        </span>
                                    </Tooltip>
                                ) : (
                                    <Typography variant="caption" sx={{ color: '#90a4ae', fontStyle: 'italic', fontSize: '0.75rem' }}>
                                        {chemoTranslations.table.dateEditNotAllowed}
                                    </Typography>
                                )}
                            </TableCell>
                            <TableCell align="center" sx={{ ...styles.tableCell, fontWeight: 700, color: '#3498db' }}>{record.ORDBAL}</TableCell>
                            <TableCell align="center" sx={styles.tableCell}>{formatDate(record.LASTDUEDATE)}</TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    </TableContainer>
);
};

const MobileCards = ({ fetchedRecords, isRecordLocked, getPreferredDueDate, handleCheckboxChange, handleFieldChange, formatDate, hospital }) => {
const canEditDate = HOSPITALS_WITH_DATE_EDIT.includes(hospital);
return (
    <>
        {fetchedRecords.map((record, index) => {
            const lockStatus = isRecordLocked(record);
            const preferredDueDate = getPreferredDueDate(record);
            return (
                <Paper key={index} sx={{
                    padding: { xs: 2, sm: 2.5 }, marginBottom: 2,
                    border: lockStatus.locked ? '2px solid #ef5350' : '1px solid #e3f2fd',
                    opacity: lockStatus.locked ? 0.7 : 1,
                    borderRadius: { xs: '8px', md: '10px' }, boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                }}>
                    {lockStatus.locked && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2, padding: 1.5, backgroundColor: '#ffebee', borderRadius: '6px' }}>
                            <LockIcon sx={{ color: '#ef5350', fontSize: 20 }} />
                            <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 600 }}>{lockStatus.reason}</Typography>
                        </Box>
                    )}
                    <Grid container spacing={2}>
                        <Grid item xs={6}><Typography variant="caption" color="textSecondary">{chemoTranslations.table.row}</Typography><Typography variant="body1" sx={{ fontWeight: 600, color: '#3498db' }}>{record.KLINE2}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="caption" color="textSecondary">{chemoTranslations.table.deliveryDate}</Typography><Typography variant="body2">{formatDate(preferredDueDate)}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="caption" color="textSecondary">{chemoTranslations.table.customerNumber}</Typography><Typography variant="body2" sx={{ fontWeight: 600, color: '#3498db' }}>{record.CUSTNAME}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="caption" color="textSecondary">{chemoTranslations.table.day}</Typography><Typography variant="body2">{record.DAY}</Typography></Grid>
                        <Grid item xs={12}><Typography variant="caption" color="textSecondary">{chemoTranslations.table.customerName}</Typography><Typography variant="body2">{record.CDES}</Typography></Grid>
                        <Grid item xs={12}>
                            <Box sx={{ padding: 1.5, backgroundColor: '#f8fafb', borderRadius: '6px', border: '1px solid #e8edf2' }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#546e7a', display: 'block', marginBottom: 1 }}>{chemoTranslations.table.status}</Typography>
                                {[
                                    ['APPROVED', chemoTranslations.table.willArrive, '#e8f5e9', '#388e3c'],
                                    ['NOTAPPROVED', chemoTranslations.table.willNotArrive, '#ffebee', '#d32f2f'],
                                    ['FINISH', chemoTranslations.table.endTreatment, '#e8eaf6', '#3949ab']
                                ].map(([field, label, bg, color]) => {
                                    const isChecked = convertToBoolean(record[field]);
                                    return (
                                        <Box key={field} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: '6px', borderBottom: '1px solid #f1f5f9', '&:last-child': { borderBottom: 'none', pb: 0 } }}>
                                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#475569' }}>{label}</Typography>
                                            <Chip
                                                size="small"
                                                label={isChecked ? '✓' : '—'}
                                                onClick={() => !(lockStatus.locked || lockStatus.statusLocked) && handleCheckboxChange(index, field)}
                                                sx={{
                                                    height: 26,
                                                    minWidth: 40,
                                                    fontSize: isChecked ? '0.85rem' : '0.8rem',
                                                    fontWeight: 700,
                                                    cursor: (lockStatus.locked || lockStatus.statusLocked) ? 'default' : 'pointer',
                                                    transition: 'all 0.15s ease',
                                                    backgroundColor: isChecked ? bg : 'transparent',
                                                    color: isChecked ? color : '#cbd5e0',
                                                    border: isChecked ? 'none' : '1px dashed #d1d5db',
                                                    boxShadow: isChecked ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                                                    '&:hover': { opacity: (lockStatus.locked || lockStatus.statusLocked) ? 1 : 0.75 },
                                                    '& .MuiChip-label': { px: '10px' }
                                                }}
                                            />
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', marginBottom: 0.5 }}>{chemoTranslations.table.updateDeliveryDate}</Typography>
                            {canEditDate ? (
                                <>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        format="DD/MM/YYYY"
                                        value={formatDateForInput(record.TODUEDATE) ? dayjs(formatDateForInput(record.TODUEDATE)) : null}
                                        onChange={(val) => !(lockStatus.locked || lockStatus.dateLocked) && handleFieldChange(index, 'TODUEDATE', val ? val.format('YYYY-MM-DD') : '')}
                                        disabled={lockStatus.locked || lockStatus.dateLocked}
                                        slotProps={{ ...datePickerPopupSlotProps, textField: { size: 'small', fullWidth: true, sx: styles.datePickerField } }}
                                    />
                                    </LocalizationProvider>
                                    {lockStatus.dateLocked && (
                                        <Typography variant="caption" sx={{ color: '#d32f2f', fontStyle: 'italic', fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
                                            {lockStatus.dateReason}
                                        </Typography>
                                    )}
                                </>
                            ) : (
                                <Typography variant="body2" sx={{ color: '#90a4ae', fontStyle: 'italic', fontSize: '0.8rem', mt: 0.5 }}>
                                    {chemoTranslations.table.dateEditNotAllowed}
                                </Typography>
                            )}
                        </Grid>
                        <Grid item xs={6}><Typography variant="caption" color="textSecondary">{chemoTranslations.table.remainingDoses}</Typography><Typography variant="body1" sx={{ fontWeight: 700, color: '#3498db' }}>{record.ORDBAL}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="caption" color="textSecondary">{chemoTranslations.table.lastDate}</Typography><Typography variant="body2">{formatDate(record.LASTDUEDATE)}</Typography></Grid>
                    </Grid>
                </Paper>
            );
        })}
    </>
);
};

const WarningDialog = ({ open, unmodifiedRecords, onClose, onSaveAnyway, getUnchangedRecordsMessage }) => (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth dir="rtl"
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' } }}>
        <Box sx={{ background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '50%', p: 0.75, display: 'flex' }}>
                    <WarningIcon sx={{ color: '#fff', fontSize: 22 }} />
                </Box>
                <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem' }}>
                    {chemoTranslations.warnings.unchangedRecordsTitle}
                </Typography>
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' } }}>
                <CloseIcon fontSize="small" />
            </IconButton>
        </Box>
        <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
            <Typography sx={{ color: '#546e7a', fontSize: '0.9rem', mb: 2, lineHeight: 1.6 }}>
                {getUnchangedRecordsMessage(unmodifiedRecords.length)}
            </Typography>
            <Box sx={{ maxHeight: '220px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                {unmodifiedRecords.map((record, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, backgroundColor: '#f0f4ff', borderRadius: '10px', border: '1px solid #c5cae9' }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#1976d2', flexShrink: 0 }} />
                        <Typography sx={{ fontWeight: 600, color: '#37474f', fontSize: '0.88rem' }}>
                            {record.CUSTNAME || record.PARTNAME}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1.5, justifyContent: 'flex-start' }}>
            <Button onClick={onSaveAnyway} variant="contained" sx={{ borderRadius: '10px', px: 3, py: 1, fontWeight: 700, backgroundColor: '#1976d2', boxShadow: '0 4px 14px rgba(25,118,210,0.35)', '&:hover': { backgroundColor: '#1565c0', boxShadow: '0 6px 18px rgba(25,118,210,0.45)' }, textTransform: 'none', fontSize: '0.9rem' }}>
                {chemoTranslations.warnings.saveAnyway}
            </Button>
            <Button onClick={onClose} variant="outlined" sx={{ borderRadius: '10px', px: 3, py: 1, fontWeight: 600, borderColor: '#d1d5db', color: '#546e7a', '&:hover': { borderColor: '#9ca3af', backgroundColor: '#f9fafb' }, textTransform: 'none', fontSize: '0.9rem' }}>
                {chemoTranslations.warnings.backToEdit}
            </Button>
        </DialogActions>
    </Dialog>
);

const DateWarningDialog = ({ open, differentDates, onClose, onContinue }) => (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth dir="rtl"
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' } }}>
        <Box sx={{ background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '50%', p: 0.75, display: 'flex' }}>
                    <CalendarIcon sx={{ color: '#fff', fontSize: 22 }} />
                </Box>
                <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem' }}>
                    {chemoTranslations.warnings.differentDatesTitle}
                </Typography>
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' } }}>
                <CloseIcon fontSize="small" />
            </IconButton>
        </Box>
        <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
            <Typography sx={{ color: '#546e7a', fontSize: '0.9rem', mb: 2, lineHeight: 1.6 }}>
                {chemoTranslations.warnings.differentDatesMessage}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {differentDates.map((date, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, backgroundColor: '#f0f4ff', borderRadius: '10px', border: '1px solid #c5cae9' }}>
                        <CalendarIcon sx={{ fontSize: 18, color: '#1976d2', flexShrink: 0 }} />
                        <Typography sx={{ fontWeight: 600, color: '#37474f', fontSize: '0.88rem' }}>
                            {date}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1.5, justifyContent: 'flex-start' }}>
            <Button onClick={onContinue} variant="contained" sx={{ borderRadius: '10px', px: 3, py: 1, fontWeight: 700, backgroundColor: '#1976d2', boxShadow: '0 4px 14px rgba(25,118,210,0.35)', '&:hover': { backgroundColor: '#1565c0', boxShadow: '0 6px 18px rgba(25,118,210,0.45)' }, textTransform: 'none', fontSize: '0.9rem' }}>
                {chemoTranslations.warnings.continueAnyway}
            </Button>
            <Button onClick={onClose} variant="outlined" sx={{ borderRadius: '10px', px: 3, py: 1, fontWeight: 600, borderColor: '#d1d5db', color: '#546e7a', '&:hover': { borderColor: '#9ca3af', backgroundColor: '#f9fafb' }, textTransform: 'none', fontSize: '0.9rem' }}>
                {chemoTranslations.warnings.backToEdit}
            </Button>
        </DialogActions>
    </Dialog>
);

export default ChemoReportForm;
